import { useState, useEffect } from 'react';
import { useAuth } from '@/state/auth/AuthProvider';
import { unifiedRBACService } from '@/services/unifiedRBACService';

/**
 * Hook to check transformation permissions for the current user
 * 
 * Determines if the user has UPDATE permission for transformations.
 * This is used to determine read-only mode:
 * - If user has UPDATE permission: editable mode
 * - If user only has READ permission: read-only mode
 * 
 * Uses the RBAC system to check permissions on the TRANSFORMATIONS screen.
 * This works for both:
 * 1. Users who explicitly click "view" (no edit permission needed)
 * 2. Users who have read-only access by default (RBAC-based)
 * 
 * @returns Object with permission flags
 */
export const useTransformationPermissions = () => {
  const { user } = useAuth();
  const [canUpdate, setCanUpdate] = useState<boolean>(false);
  const [canCreate, setCanCreate] = useState<boolean>(false);
  const [canDelete, setCanDelete] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    const checkPermissions = async () => {
      if (!user?.id) {
        setCanUpdate(false);
        setCanCreate(false);
        setCanDelete(false);
        setIsLoading(false);
        return;
      }

      try {
        // Check permissions on TRANSFORMATIONS screen using screen code
        // This is the secure way - permissions are checked server-side via RBAC
        const [hasUpdate, hasCreate, hasDelete] = await Promise.all([
          unifiedRBACService.checkScreenPermissionByCode(user.id, 'TRANSFORMATIONS', 'UPDATE'),
          unifiedRBACService.checkScreenPermissionByCode(user.id, 'TRANSFORMATIONS', 'CREATE'),
          unifiedRBACService.checkScreenPermissionByCode(user.id, 'TRANSFORMATIONS', 'DELETE')
        ]);

        setCanUpdate(hasUpdate);
        setCanCreate(hasCreate);
        setCanDelete(hasDelete);
      } catch (error) {
        console.error('Error checking transformation permissions:', error);
        // On error, default to read-only (secure default)
        setCanUpdate(false);
        setCanCreate(false);
        setCanDelete(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkPermissions();
  }, [user?.id]);

  return {
    canUpdate,
    canCreate,
    canDelete,
    canRead: true, // Assume READ permission if user can see the page (RBAC route protection handles this)
    isReadOnly: !canUpdate, // Read-only if user doesn't have UPDATE permission
    isLoading
  };
};

